import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import { Modal, Field } from "./Fields";
import { api, fmtUSD, fmtNum, todayISO } from "../lib/api";
import { downloadCsv } from "../lib/csv";
import { useYear } from "../context/YearContext";
import AddableSelect from "../components/AddableSelect";
import { Plus, Route, Trash2, Pencil, Download } from "lucide-react";
import { toast } from "sonner";

const RATE = 0.67;
const blank = { date: todayISO(), purpose: "", miles: "", vehicle: "", business_pct: 100, notes: "" };

export default function Mileage() {
  const { year, refreshYears } = useYear();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = async () => {
    const { data } = await api.get(`/mileage?year=${year}`);
    setRows(data);
  };

  useEffect(() => {
    load();
  }, [year]);

  // Mobile FAB entry point
  useEffect(() => {
    if (searchParams.get("quick") === "1") {
      setEditing(null);
      setForm({ ...blank, date: todayISO() });
      setOpen(true);
      searchParams.delete("quick");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const totalMiles = rows.reduce((s, r) => s + r.miles * (r.business_pct / 100), 0);
  const deduction = totalMiles * RATE;

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      miles: parseFloat(form.miles) || 0,
      business_pct: parseFloat(form.business_pct) || 0,
      year,
    };
    try {
      let res;
      if (editing) {
        res = await api.put(`/mileage/${editing.id}`, payload);
        toast.success(res?.data?._offline_draft ? "Saved offline — will sync when back online" : "Mileage updated");
      } else {
        res = await api.post("/mileage", payload);
        toast.success(res?.data?._offline_draft ? "Saved offline — will sync when back online" : "Trip logged");
      }
      setOpen(false);
      setEditing(null);
      setForm({ ...blank, date: todayISO() });
      refreshYears();
      load();
    } catch {
      toast.error("Could not save trip");
    }
  };

  const onEdit = (r) => {
    setEditing(r);
    setForm({
      date: r.date,
      purpose: r.purpose,
      miles: r.miles,
      vehicle: r.vehicle,
      business_pct: r.business_pct,
      notes: r.notes || "",
    });
    setOpen(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this trip?")) return;
    await api.delete(`/mileage/${id}`);
    toast.success("Trip deleted");
    load();
  };

  return (
    <div>
      <TopBar
        title="Mileage Log"
        subtitle={`Business miles for the truck, the pickup, the service vehicle. ${year} standard rate: $${RATE}/mile.`}
        actions={
          <div className="flex items-center gap-2 mt-6">
            <button
              data-testid="export-mileage-csv"
              onClick={() => downloadCsv(`mileage-${year}.csv`, rows.map((r) => ({
                date: r.date, vehicle: r.vehicle, purpose: r.purpose,
                miles: r.miles, business_pct: r.business_pct,
                deductible_miles: (r.miles * r.business_pct / 100).toFixed(2),
                deduction_usd: (r.miles * r.business_pct / 100 * RATE).toFixed(2),
                notes: r.notes || "",
              })))}
              disabled={!rows.length}
              className="border border-[#1F1E1B] px-4 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#1F1E1B] hover:text-white transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
            <button
              data-testid="add-mileage-btn"
              onClick={() => { setEditing(null); setForm({ ...blank, date: todayISO() }); setOpen(true); }}
              className="bg-[#BC4727] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#9D391E] transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Log Trip
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <div className="bg-white border border-[#1F1E1B] p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Total Business Miles</div>
          <div className="font-display text-3xl font-black tabular">{fmtNum(totalMiles, 1)}</div>
        </div>
        <div className="bg-[#1A4135] text-white p-4 border border-[#1A4135]">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C8D5CE]">Tax Deduction @ ${RATE}</div>
          <div className="font-display text-3xl font-black tabular">{fmtUSD(deduction)}</div>
        </div>
        <div className="bg-white border border-[#1F1E1B] p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Logged Trips</div>
          <div className="font-display text-3xl font-black tabular">{rows.length}</div>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={Route} title="No trips logged" hint="Log a trip every time you drive for farm business — supplies runs, co-op, fuel station, equipment pickup." testid="empty-mileage" />
      ) : (
        <div className="bg-white border border-[#1F1E1B] overflow-x-auto">
          <table className="ledger-table" data-testid="mileage-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Vehicle</th>
                <th>Purpose</th>
                <th className="num">Miles</th>
                <th className="num">Business %</th>
                <th className="num">Ded. Miles</th>
                <th className="num">$ @ {RATE}</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const ded = r.miles * (r.business_pct / 100);
                return (
                  <tr key={r.id} data-testid={`mileage-row-${r.id}`}>
                    <td className="tabular">{r.date}</td>
                    <td className="font-semibold">{r.vehicle}</td>
                    <td className="text-[#66645E]">{r.purpose}</td>
                    <td className="num">{fmtNum(r.miles, 1)}</td>
                    <td className="num">{r.business_pct}%</td>
                    <td className="num font-semibold">{fmtNum(ded, 1)}</td>
                    <td className="num font-bold">{fmtUSD(ded * RATE)}</td>
                    <td>
                      <div className="flex gap-1.5 justify-end">
                        <button data-testid={`edit-mileage-${r.id}`} onClick={() => onEdit(r)} className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white transition-colors"><Pencil className="w-3 h-3" /></button>
                        <button data-testid={`delete-mileage-${r.id}`} onClick={() => onDelete(r.id)} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white transition-colors"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <Modal title={editing ? "Edit Trip" : "Log Trip"} onClose={() => setOpen(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="mileage-form">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Date" required>
                <input data-testid="mileage-date-input" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Vehicle" required>
                <AddableSelect
                  domain="vehicle"
                  defaults={[]}
                  value={form.vehicle}
                  onChange={(v) => setForm({ ...form, vehicle: v })}
                  testid="mileage-vehicle-input"
                  required
                  placeholder="— Select vehicle —"
                />
              </Field>
            </div>
            <Field label="Purpose" required>
              <input data-testid="mileage-purpose-input" required value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Co-op run, grain elevator, parts pickup..." className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Miles" required>
                <input data-testid="mileage-miles-input" type="number" step="0.1" required value={form.miles} onChange={(e) => setForm({ ...form, miles: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
              <Field label="Business %">
                <input data-testid="mileage-pct-input" type="number" step="1" min="0" max="100" value={form.business_pct} onChange={(e) => setForm({ ...form, business_pct: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
            </div>
            <Field label="Notes">
              <textarea data-testid="mileage-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} data-testid="mileage-cancel-btn" className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="mileage-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Trip</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
